import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { VideoPlayer } from '../VideoPlayer';

type Metric = {
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
};

interface ChannelWorkspaceHeaderProps {
  title: string;
  modeLabel: string;
  statusLabel: string;
  statusTone?: 'default' | 'success' | 'warning';
  sourceLabel: string;
  primaryLabel: string;
  primaryValue: React.ReactNode;
  secondaryLabel: string;
  secondaryValue: React.ReactNode;
  previewTypeControl?: React.ReactNode;
  subtitle?: React.ReactNode;
  metrics: Metric[];
  actions?: React.ReactNode;
  preview: {
    channelId?: number | null;
    channelHashId?: string | null;
    streamName: string;
    streamKeyPin: string;
    isLive: boolean;
    previewType?: 'webrtc' | 'hls';
  };
}

const metricToneColor = (tone?: Metric['tone']) => {
  if (tone === 'success') return 'var(--success)';
  if (tone === 'warning') return 'var(--warning)';
  return 'var(--text-main)';
};

const statusToneStyles = (tone?: ChannelWorkspaceHeaderProps['statusTone']) => {
  if (tone === 'success') {
    return {
      bg: 'rgba(var(--success-rgb),0.14)',
      color: 'var(--success)',
      border: 'rgba(var(--success-rgb),0.32)',
    };
  }
  if (tone === 'warning') {
    return {
      bg: 'rgba(var(--warning-rgb),0.14)',
      color: 'var(--warning)',
      border: 'rgba(var(--warning-rgb),0.32)',
    };
  }
  return {
    bg: 'var(--primary-light)',
    color: 'var(--text-main)',
    border: 'var(--border-strong)',
  };
};

export const ChannelWorkspaceHeader: React.FC<ChannelWorkspaceHeaderProps> = ({
  title,
  modeLabel,
  statusLabel,
  statusTone = 'default',
  sourceLabel,
  primaryLabel,
  primaryValue,
  secondaryLabel,
  secondaryValue,
  previewTypeControl,
  subtitle,
  metrics,
  actions,
  preview,
}) => {
  const statusStyles = statusToneStyles(statusTone);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 2fr) minmax(360px, 1fr)' },
          gap: 0,
        }}
      >
        <Box sx={{ p: { xs: 1.2, sm: 1.6, lg: 2 }, minWidth: 0 }}>
          <Stack spacing={1.2}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              justifyContent="space-between"
              spacing={1.2}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 0.65 }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={900}
                    sx={{ color: 'text.primary', lineHeight: 1.1 }}
                  >
                    {title}
                  </Typography>
                  <Chip
                    label={modeLabel}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      fontWeight: 800,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  />
                  <Chip
                    label={statusLabel}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      fontWeight: 900,
                      bgcolor: statusStyles.bg,
                      color: statusStyles.color,
                      border: '1px solid',
                      borderColor: statusStyles.border,
                    }}
                  />
                </Stack>
                <Typography
                  sx={{
                    fontSize: '0.84rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mb: subtitle ? 0.5 : 0,
                  }}
                >
                  Source: {sourceLabel}
                </Typography>
                {subtitle ? (
                  <Typography sx={{ color: 'text.secondary', maxWidth: 820 }}>
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>

              {actions ? (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                  justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
                >
                  {actions}
                </Stack>
              ) : null}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  p: 1.15,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 900,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                    mb: 0.65,
                  }}
                >
                  {primaryLabel}
                </Typography>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: 'text.primary' }}>
                  {primaryValue}
                </Typography>
              </Box>
              {previewTypeControl ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25, minWidth: 0 }}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                      p: 1.15,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 900,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                        mb: 0.65,
                      }}
                    >
                      {secondaryLabel}
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: 'text.primary' }}>
                      {secondaryValue}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                      p: 1.15,
                      minWidth: 0,
                    }}
                  >
                    {previewTypeControl}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    p: 1.15,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 900,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      mb: 0.65,
                    }}
                  >
                    {secondaryLabel}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: 'text.primary' }}>
                    {secondaryValue}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {metrics.map((metric) => (
                <Box
                  key={metric.label}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    px: 1.1,
                    py: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 900,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                    }}
                    noWrap
                  >
                    {metric.label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.55,
                      fontSize: '0.9rem',
                      fontWeight: 900,
                      color: metricToneColor(metric.tone),
                    }}
                    noWrap
                  >
                    {metric.value}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.2,
                      fontSize: '0.72rem',
                      color: 'text.secondary',
                    }}
                    noWrap
                  >
                    {metric.helper || '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            borderTop: { xs: '1px solid', xl: 0 },
            borderLeft: { xs: 0, xl: '1px solid' },
            borderColor: 'divider',
            bgcolor: 'background.default',
            minHeight: { xs: 220, sm: 280, xl: '100%' },
          }}
        >
          <Box sx={{ height: '100%', minHeight: { xs: 220, sm: 280, xl: 380 } }}>
            <VideoPlayer
              channelId={preview.channelId}
              channelHashId={preview.channelHashId}
              streamName={preview.streamName}
              streamKeyPin={preview.streamKeyPin}
              isLive={preview.isLive}
              previewType={preview.previewType}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
